<template v-if="isVisible">
    <div class="popup-overlay" :class="classes.overlay" :style="computedOverlayStyle" @click="handleOverlayClick">
        <div class="popup-wrapper" :class="classes.wrapper" :style="computedWrapperStyle" @click.stop>
            <!-- Close button - separate from header -->
            <button
                v-if="options.showCloseButton && !options.pureContent"
                type="button"
                class="btn-close-popup"
                :class="classes.closeButton"
                :style="computedCloseButtonStyle"
                @click="handleClose"
            >
                <i class="fa-solid fa-xmark"></i>
            </button>

            <!-- Header -->
            <div v-if="options.showHeader" class="popup-header height-50-px" :class="classes.header" :style="computedHeaderStyle">
                <img v-if="options.showLogo" :src="computedLogoSrc" alt="Logo" class="popup-logo" :class="classes.logo" :style="computedLogoStyle" />
                <span class="popup-title" :class="classes.title" :style="computedTitleStyle">{{ title }}</span>
            </div>

            <!-- Body -->
            <div class="popup-body" :class="[classes.body, { 'pure-content': options.pureContent }]" :style="computedBodyStyle">
                <slot></slot>
            </div>

            <!-- Footer -->
            <div v-if="options.showFooter" class="popup-footer" :class="classes.footer" :style="computedFooterStyle">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AnsPopup',
    props: {
        isVisible: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        logoSrc: {
            type: String,
            default: ''
        },
        dimensions: {
            type: Object,
            default: () => ({
                width: '75%',
                height: '90%',
                maxWidth: null,
                maxHeight: null
            })
        },
        styles: {
            type: Object,
            default: () => ({
                overlay: {},
                wrapper: {},
                header: {},
                logo: {},
                title: {},
                body: {},
                footer: {},
                closeButton: {}
            })
        },
        classes: {
            type: Object,
            default: () => ({
                overlay: '',
                wrapper: '',
                header: '',
                logo: '',
                title: '',
                body: '',
                footer: '',
                closeButton: ''
            })
        },
        options: {
            type: Object,
            default: () => ({
                allowClickOverlay: true,
                showHeader: true,
                showLogo: false,
                showCloseButton: true,
                showFooter: false,
                closeOnOverlay: true,
                closeOnEscape: true,
                pureContent: false
            })
        }
    },
    computed: {
        computedLogoSrc() {
            return this.logoSrc || require('@/assets/images/common/moveLogo.png');
        },
        computedWrapperStyle() {
            const style = { ...this.styles.wrapper };

            if (this.dimensions.width) {
                style.width = typeof this.dimensions.width === 'number' ? `${this.dimensions.width}px` : this.dimensions.width;
            }

            if (this.dimensions.height) {
                style.height = typeof this.dimensions.height === 'number' ? `${this.dimensions.height}px` : this.dimensions.height;
            }

            if (this.dimensions.maxWidth) {
                style.maxWidth = typeof this.dimensions.maxWidth === 'number' ? `${this.dimensions.maxWidth}px` : this.dimensions.maxWidth;
            }

            if (this.dimensions.maxHeight) {
                style.maxHeight = typeof this.dimensions.maxHeight === 'number' ? `${this.dimensions.maxHeight}px` : this.dimensions.maxHeight;
            }

            return style;
        },
        computedBodyStyle() {
            const style = { ...this.styles.body };

            // Calculate height based on header and footer
            let headerHeight = this.options.showHeader ? 55 : 0; // header height
            let footerHeight = this.options.showFooter ? 55 : 0; // footer height
            let totalFixedHeight = headerHeight + footerHeight;

            if (totalFixedHeight > 0) {
                style.maxHeight = `calc(100% - ${totalFixedHeight}px)`;
            } else {
                style.maxHeight = '100%';
            }

            return style;
        },
        computedOverlayStyle() {
            return { ...this.styles.overlay };
        },
        computedHeaderStyle() {
            return { ...this.styles.header };
        },
        computedLogoStyle() {
            const baseStyle = {
                backgroundColor: '#f8f9fa'
            };
            return { ...baseStyle, ...this.styles.logo };
        },
        computedTitleStyle() {
            return { ...this.styles.title };
        },
        computedFooterStyle() {
            return { ...this.styles.footer };
        },
        computedCloseButtonStyle() {
            return { ...this.styles.closeButton };
        }
    },
    mounted() {
        if (this.options.closeOnEscape) {
            document.addEventListener('keydown', this.handleEscapeKey);
        }
    },
    unmounted() {
        if (this.options.closeOnEscape) {
            document.removeEventListener('keydown', this.handleEscapeKey);
        }
        // Ensure body scroll is restored when component is destroyed
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
    },
    watch: {
        isVisible: {
            handler(newVal) {
                this.$nextTick(() => {
                    if (newVal) {
                        // Prevent body scroll when popup is open
                        document.body.style.overflow = 'hidden';
                        document.documentElement.style.overflow = 'hidden';
                    } else {
                        // Restore body scroll when popup is closed
                        document.body.style.overflow = '';
                        document.documentElement.style.overflow = '';
                    }
                });
            },
            immediate: true
        }
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
        handleOverlayClick() {
            if (this.options.closeOnOverlay && this.options.allowClickOverlay) {
                this.handleClose();
            }
        },
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.isVisible) {
                this.handleClose();
            }
        }
    }
};
</script>

<style scoped>
.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    transition: opacity 0.15s linear;
}

.popup-wrapper {
    position: relative;
    background-color: white;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.popup-wrapper:has(.popup-body.pure-content) {
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
    margin: 0;
}

.btn-close-popup {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #666;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
    z-index: 10;
}

.btn-close-popup:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
}

.popup-header {
    padding: 16px 50px 16px 16px;
    border-bottom: 1px solid #dee2e6;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
}

.popup-logo {
    width: 80px;
    height: 50px;
    object-fit: cover;
}

.popup-title {
    font-weight: 600;
    font-size: 18px;
    color: #333;
    margin: 0;
}

.popup-body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

.popup-body.pure-content {
    padding: 0;
    margin: 0;
    overflow: hidden;
    border: none;
    background: none;
}

.popup-footer {
    padding: 16px;
    border-top: 1px solid #dee2e6;
    background-color: #f8f9fa;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

/* Responsive */
@media (max-width: 768px) {
    .popup-wrapper {
        width: 95% !important;
        height: 95% !important;
        max-width: none !important;
        max-height: none !important;
    }

    .popup-title {
        font-size: 16px;
    }
}
</style>
